import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import gql from 'graphql-tag'
import { withApollo } from '@apollo/client/react/hoc'
import { injectIntl, FormattedMessage, IntlProvider } from 'react-intl'
import {
	Modal,
	Form,
	Input,
	Button,
	Switch,
	Radio,
	Select,
	message
} from 'antd'

import { forgotPassword } from '../../auth'
import { formItemLayout } from '../../components/Styles'
import { TeamDetailsQuery, TeamsTreeQuery } from '../teams/Queries'
import * as Auth from '../../auth'
import * as Utils from '../../components/Utils'

const FormItem = Form.Item
const RadioButton = Radio.Button
const RadioGroup = Radio.Group

const roles = {
	setRole: ['manager', 'user'],
	setAdminRole: ['superadmin', 'admin', 'manager', 'user']
}

class NewUserModal extends Component {
	formRef = React.createRef()

	state = {
		confirmDirty: false,
		loading: false,
		role: this.props.user ? this.props.user.role : 'user',
		montyUser: this.props.user ? this.props.user.montyUser : null,
		language: this.props.user ? this.props.user.language || 'fr' : 'fr'
	}

	handleCancel = () => {
		this.props.onClose()
	}

	onClickSubmit = async (e) => {
		e.preventDefault()

		this.setState({ loading: true })
		const { onClose, client, team, user } = this.props
		try {
			const values = await this.formRef.current.validateFields()
			let params = {}
			let mutation = null
			if (user) {
				params = {
					...values,
					email: values.email.toLowerCase(),
					role: this.state.role,
					id: user._id,
					montyUser: this.state.montyUser,
					language: this.state.language,
					nicknackSpotifyAppClientId:
						this.state.montyUser ?
							values.nicknackSpotifyAppClientId
						:	null
				}
				mutation = gql`
					mutation updateUser(
						$id: String!
						$firstName: String
						$lastName: String
						$role: String
						$email: String
						$montyUser: Boolean
						$nicknackSpotifyAppClientId: String
						$language: String
					) {
						userUpdateById(
							_id: $id
							record: {
								firstName: $firstName
								lastName: $lastName
								role: $role
								username: $email
								montyUser: $montyUser
								nicknackSpotifyAppClientId: $nicknackSpotifyAppClientId
								language: $language
							}
						) {
							record {
								username
							}
						}
					}
				`
			} else {
				params = {
					...values,
					email: values.email.toLowerCase(),
					role: this.state.role,
					teamId: team._id,
					montyUser: this.state.montyUser,
					language: this.state.language,
					notifyOfflineType: 2,
					notifyOfflineDelay: 24 * 7,
					notifyActivationType: 2,
					notifyDisabledType: 2
				}
				mutation = gql`
					mutation createUser(
						$firstName: String!
						$lastName: String!
						$role: String
						$email: String!
						$teamId: String!
						$montyUser: Boolean
						$language: String
						$notifyOfflineType: Float
						$notifyOfflineDelay: Float
						$notifyActivationType: Float
						$notifyDisabledType: Float
						$nicknackSpotifyAppClientId: String
					) {
						userCreate(
							record: {
								firstName: $firstName
								lastName: $lastName
								role: $role
								username: $email
								teamId: $teamId
								montyUser: $montyUser
								language: $language
								notifyOfflineType: $notifyOfflineType
								notifyOfflineDelay: $notifyOfflineDelay
								notifyActivationType: $notifyActivationType
								notifyDisabledType: $notifyDisabledType
								nicknackSpotifyAppClientId: $nicknackSpotifyAppClientId
							}
						) {
							success
							message
							user {
								id
								name
							}
							teamInfos {
								id
								name
							}
						}
					}
				`
			}

			const result = await client.mutate({
				variables: params,
				mutation: mutation,
				refetchQueries: [
					{
						query: TeamsTreeQuery
					},
					{
						query: TeamDetailsQuery,
						variables: { id: team._id }
					}
				]
			})
			Utils.parseMutationResponse(result)
			await Auth.refreshUser(client)
			if (!user) {
				const userCreateResult = result.data.userCreate
				if (userCreateResult?.success && userCreateResult?.user?.name) {
					const inviteresult = await forgotPassword({
						username: userCreateResult.user.name,
						invite: 'onInvite'
					})
					this.setState({ loading: false })
					if (inviteresult.success) {
						onClose()
					} else {
						console.log(inviteresult)
					}
				}
			} else {
				this.setState({ loading: false })
				onClose()
			}
		} catch (e) {
			this.setState({ loading: false })

			if (e.errorFields) {
				// antd form validation error
				return
			}

			// specific error when the user already exists
			if (e.message === 'USER_ALREADY_EXISTS') {
				message.open({
					type: 'error',
					content: (
						<IntlProvider
							locale={Utils.language}
							messages={Utils.messages[Utils.language]}
						>
							<FormattedMessage
								id={
									e.data?.teamInfos ?
										'teams.addUser.alreadyExistsWithTeamDetails'
									:	'teams.addUser.alreadyExists'
								}
								values={{
									email: e.data?.user?.name,
									teamLink: (
										<a
											onClick={() =>
												this.props.history.push(
													`/teams/${e.data?.teamInfos?.id}`
												)
											}
										>
											{e.data?.teamInfos?.name}
										</a>
									)
								}}
							/>
						</IntlProvider>
					)
				})
			} else {
				Utils.displayError(e)
			}
		}
	}

	handleConfirmBlur = (e) => {
		const value = e.target.value
		this.setState({ confirmDirty: this.state.confirmDirty || !!value })
	}

	render() {
		const { intl, team, user } = this.props

		let setRole = null
		if (Auth.hasAccess('user:edit', team._id)) {
			if (Auth.hasPermission('user:edit:setAdminRole')) {
				setRole = 'setAdminRole'
			} else if (Auth.hasPermission('user:edit:setRole')) {
				setRole = 'setRole'
			}
		}

		let canEdit =
			(Auth.hasAccess('user:edit', team._id) &&
				Auth.hasPermission('user:edit:setInfo')) ||
			user._id === Auth.cache.user._id

		return (
			<Modal
				visible={true}
				width="80vw"
				title={
					user ?
						intl.formatMessage(
							{ id: 'teams.editUser.title' },
							{ name: user.username }
						)
					:	intl.formatMessage(
							{ id: 'teams.addUser.title' },
							{ team: team.n }
						)
				}
				onCancel={this.handleCancel}
				footer={[
					<Button
						type="primary"
						htmlType="submit"
						key="submit"
						onClick={this.onClickSubmit}
						loading={this.state.loading}
					>
						<FormattedMessage
							id={user ? 'actions.save' : 'teams.addUser.submit'}
						/>
					</Button>
				]}
			>
				<Form ref={this.formRef}>
					{canEdit && (
						<FormItem
							{...formItemLayout}
							name="email"
							initialValue={user && user.username}
							label={intl.formatMessage({
								id: 'teams.details.email'
							})}
							rules={[
								{
									type: 'email',
									message: intl.formatMessage({
										id: 'teams.addUser.emailInvalid'
									})
								},
								{
									required: true,
									message: intl.formatMessage({
										id: 'teams.addUser.emailEmpty'
									})
								}
							]}
						>
							<Input
								autoCapitalize="none"
								autocompletetype="email"
								autoCorrect="false"
								keyboardtype="email-address"
							/>
						</FormItem>
					)}
					{canEdit && (
						<FormItem
							{...formItemLayout}
							name="firstName"
							initialValue={user && user.firstName}
							label={intl.formatMessage({
								id: 'teams.details.firstName'
							})}
							rules={[
								{
									required: true,
									message: intl.formatMessage({
										id: 'teams.addUser.firstNameEmpty'
									}),
									whitespace: true
								}
							]}
						>
							<Input />
						</FormItem>
					)}
					{canEdit && (
						<FormItem
							{...formItemLayout}
							name="lastName"
							initialValue={user && user.lastName}
							label={intl.formatMessage({
								id: 'teams.details.lastName'
							})}
							rules={[
								{
									required: true,
									message: intl.formatMessage({
										id: 'teams.addUser.lastNameEmpty'
									}),
									whitespace: true
								}
							]}
						>
							<Input />
						</FormItem>
					)}
					{canEdit && (
						<FormItem
							{...formItemLayout}
							label={intl.formatMessage({
								id: 'teams.details.language'
							})}
							data-test="user-language-select"
						>
							<Select
								value={this.state.language}
								onChange={(v) => this.setState({ language: v })}
							>
								{Utils.languages.map((l) => (
									<Select.Option key={l} value={l}>
										<FormattedMessage id={`lang.${l}`} />
									</Select.Option>
								))}
							</Select>
						</FormItem>
					)}
					{setRole && (
						<FormItem
							{...formItemLayout}
							label={intl.formatMessage({
								id: 'teams.addUser.role'
							})}
						>
							<RadioGroup
								value={this.state.role}
								onChange={(v) =>
									this.setState({ role: v.target.value })
								}
							>
								{roles[setRole].map((r, i) => (
									<RadioButton value={r} key={`role-${r}`}>
										{r}
									</RadioButton>
								))}
							</RadioGroup>
						</FormItem>
					)}
					{Auth.hasAccess('user:edit', team._id) &&
						Auth.hasPermission('user:edit:setMontyRole') && (
							<FormItem
								{...formItemLayout}
								label={intl.formatMessage({
									id: 'teams.addUser.montyRole'
								})}
							>
								<Switch
									onChange={(v) =>
										this.setState({ montyUser: v })
									}
									checked={this.state.montyUser}
								/>
							</FormItem>
						)}
					{Auth.hasAccess('user:edit', team._id) &&
						Auth.hasPermission(
							'user:edit:setNicknackSpotifyAppClientId'
						) &&
						this.state.montyUser && (
							<FormItem
								{...formItemLayout}
								name="nicknackSpotifyAppClientId"
								initialValue={
									user && user.nicknackSpotifyAppClientId
								}
								label={intl.formatMessage({
									id: 'teams.addUser.nicknackSpotifyAppClientId'
								})}
							>
								<Input />
							</FormItem>
						)}
				</Form>
			</Modal>
		)
	}
}

export default withRouter(withApollo(injectIntl(NewUserModal)))
